import { memo } from 'react';
import { BaseEdge, EdgeLabelRenderer, getBezierPath, type EdgeProps } from '@xyflow/react';
import { Badge } from '@/components/ui/badge';
import { Lock, ShieldCheck } from 'lucide-react';

interface ConduitEdgeData {
  label?: string;
  direction?: 'Unidirectional' | 'Bidirectional';
  ports?: Array<{ port: number; protocol: string; description?: string }>;
  encryption?: boolean;
  inspection?: string;
}

export const ConduitEdge = memo(({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const edgeData = data as ConduitEdgeData | undefined;
  
  const portsSummary = edgeData?.ports && edgeData.ports.length > 0
    ? edgeData.ports.slice(0, 2).map(p => `${p.protocol}:${p.port}`).join(', ') +
      (edgeData.ports.length > 2 ? ` +${edgeData.ports.length - 2}` : '')
    : '';

  // Only show label container if there's meaningful user-set content
  // A new connection with just default values should show as a clean line
  const hasLabel = edgeData?.label && edgeData.label.trim() !== '' && edgeData.label !== 'Connection';
  const hasPorts = portsSummary.length > 0;
  const hasEncryption = edgeData?.encryption === true;
  const hasInspection = edgeData?.inspection && edgeData.inspection.trim() !== '';
  const hasContent = hasLabel || hasPorts || hasEncryption || hasInspection;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: selected ? 'hsl(var(--layout-edge-selected))' : 'hsl(var(--layout-edge))',
          strokeWidth: selected ? 3 : 2,
        }}
        markerEnd={edgeData?.direction === 'Unidirectional' ? 'url(#arrowhead)' : undefined}
      />
      {hasContent && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            <div className="flex flex-col items-center gap-1 bg-background rounded-lg border border-border p-2 shadow-lg">
              {hasLabel && (
                <div className="font-medium text-xs">{edgeData?.label}</div>
              )}
              {hasPorts && (
                <Badge variant="secondary" className="text-xs">
                  {portsSummary}
                </Badge>
              )}
              {(hasEncryption || hasInspection) && (
                <div className="flex items-center gap-1">
                  {hasEncryption && <Lock className="h-3 w-3 text-green-600" />}
                  {hasInspection && <ShieldCheck className="h-3 w-3 text-blue-600" />}
                </div>
              )}
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
      <defs>
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,6 L9,3 z" fill={selected ? 'hsl(var(--layout-edge-selected))' : 'hsl(var(--layout-edge))'} />
        </marker>
      </defs>
    </>
  );
});

ConduitEdge.displayName = 'ConduitEdge';
